import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { PROJECT_TYPE_COLOR } from "@/data/ekipData";

/**
 * Côte d'Ivoire real outline map (d-maps.com).
 * Image already includes city labels; we overlay colored diamond markers
 * on top of each project location.
 */
const CI_MAP_URL =
  "https://customer-assets.emergentagent.com/job_holding-refresh/artifacts/0ur4om43_IMG_0498.gif";

export default function CoteIvoireMap({ projects }) {
  const { t, lang } = useLang();
  const [hovered, setHovered] = useState(null);

  const markers = projects.flatMap((p) =>
    p.locations.map((loc) => ({
      ...loc,
      projectSlug: p.slug,
      projectTitle: p.title,
      color: PROJECT_TYPE_COLOR[p.type],
      type: p.type,
    }))
  );

  // Deduplicate markers by city name; collect all projects per city
  const cityMap = new Map();
  markers.forEach((m) => {
    const key = m.name;
    if (!cityMap.has(key)) {
      cityMap.set(key, { name: m.name, x: m.x, y: m.y, projects: [] });
    }
    cityMap.get(key).projects.push({
      slug: m.projectSlug,
      title: m.projectTitle,
      type: m.type,
      color: m.color,
    });
  });
  const cities = Array.from(cityMap.values());

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
      <div
        className="md:col-span-8 relative bg-ekip-navy-deep p-6 md:p-10"
        style={{ aspectRatio: "720 / 770" }}
        data-testid="cote-ivoire-map"
      >
        <div className="absolute top-6 left-6 z-10">
          <span className="text-[10px] uppercase tracking-[0.28em] text-ekip-copper font-medium">
            Côte d&apos;Ivoire
          </span>
          <div className="mt-1 text-[11px] text-white/60">
            {cities.length} {lang === "fr" ? "ville(s)" : "cities"} · {markers.length}{" "}
            {lang === "fr" ? "implantation(s)" : "location(s)"}
          </div>
        </div>

        {/* Background image — real d-maps.com outline, blended into navy background */}
        <img
          src={CI_MAP_URL}
          alt="Carte de la Côte d'Ivoire"
          className="absolute inset-6 md:inset-10 pointer-events-none"
          style={{
            width: "calc(100% - 3rem)",
            height: "calc(100% - 3rem)",
            filter: "invert(1) brightness(0.95)",
            mixBlendMode: "lighten",
            opacity: 0.9,
          }}
        />

        {/* Marker overlay */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-6 md:inset-10"
          style={{ width: "calc(100% - 3rem)", height: "calc(100% - 3rem)" }}
          preserveAspectRatio="none"
        >
          {cities.map((c, i) => {
            const isHovered = hovered === i;
            const primary = c.projects[0];
            const count = c.projects.length;
            return (
              <g
                key={c.name}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: "pointer" }}
                data-testid={`map-marker-${c.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/[éè]/g, "e")}`}
              >
                {/* Halo pulse */}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={isHovered ? "3.2" : "2"}
                  fill={primary.color}
                  opacity={isHovered ? "0.4" : "0.22"}
                  style={{ transition: "all 0.3s" }}
                />
                {/* Diamond marker */}
                <rect
                  x={c.x - 1.1}
                  y={c.y - 1.1}
                  width="2.2"
                  height="2.2"
                  fill={primary.color}
                  stroke="rgba(250,250,248,1)"
                  strokeWidth="0.3"
                  transform={`rotate(45 ${c.x} ${c.y})`}
                />
                {/* Count badge if multiple projects on this city */}
                {count > 1 && (
                  <g transform={`translate(${c.x + 1.5}, ${c.y - 1.5})`}>
                    <circle r="1.3" fill="#C49A3C" stroke="rgba(250,250,248,1)" strokeWidth="0.2" />
                    <text
                      y="0.5"
                      textAnchor="middle"
                      fill="#0C2340"
                      fontSize="1.7"
                      fontWeight="700"
                      fontFamily="Poppins"
                    >
                      {count}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend + hovered detail */}
      <div className="md:col-span-4 flex flex-col gap-6">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
            {lang === "fr" ? "Légende" : "Legend"}
          </span>
          <ul className="mt-5 space-y-3">
            {Object.entries(PROJECT_TYPE_COLOR).map(([type, color]) => (
              <li key={type} className="flex items-center gap-3">
                <span
                  className="w-3 h-3"
                  style={{ background: color, transform: "rotate(45deg)" }}
                />
                <span className="text-sm text-ekip-slate font-body">
                  {t.projets.map.legend[type]}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <AnimatePresence mode="wait">
          {hovered !== null && cities[hovered] && (
            <motion.div
              key={hovered}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="bg-white border-l-4 p-5"
              style={{ borderLeftColor: cities[hovered].projects[0].color }}
            >
              <span
                className="text-[10px] uppercase tracking-[0.3em] font-medium"
                style={{ color: cities[hovered].projects[0].color }}
              >
                {cities[hovered].name}
              </span>
              <p className="mt-2 text-[10px] uppercase tracking-[0.24em] text-ekip-graphite">
                {cities[hovered].projects.length}{" "}
                {lang === "fr" ? "projet(s) sur cette ville" : "project(s) in this city"}
              </p>
              <ul className="mt-3 space-y-2">
                {cities[hovered].projects.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span
                      className="mt-1.5 w-2.5 h-2.5"
                      style={{ background: p.color, transform: "rotate(45deg)" }}
                    />
                    <span className="font-display font-semibold text-ekip-navy leading-tight">
                      {p.title}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
